// src/features/admin-options/components/BackupsView.tsx
//
// Backups tab (SuperAdmin only): mounts the shared <BackupPanel> against the
// API's system backup endpoints. The endpoints live at an absolute path on the
// API host (/api/v1/system/backups), not under the UI-scoped base path
// (VITE_API_URL ends in /api/v1/ui), so only the origin is derived from it.
import React from 'react';
import { Box, Typography } from '@mui/material';
import { BackupRounded as BackupIcon } from '@mui/icons-material';
import { useLocale, useTranslate } from 'react-admin';
import { useTheme } from '@mui/material/styles';
import { BackupPanel, backupPanelThemeVars } from '@roommatik/backup-ui';
import { getToken } from '@/utils/auth';

export const BackupsView: React.FC = () => {
  const locale = useLocale();
  const translate = useTranslate();
  const theme = useTheme();

  const apiBase = `${new URL(import.meta.env.VITE_API_URL).origin}/api/v1/system/backups`;

  const auth = () => ({ Authorization: `Bearer ${getToken()}` });

  // Bridge the host v3 theme into the panel's --rmk-* CSS variables so the
  // backup panel inherits the warm-neutral surfaces, dark-hairline outline,
  // brand and status colours instead of its own fallbacks.
  return (
    <Box>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <BackupIcon color="primary" />
        <Typography variant="h6">{translate('resources.adminOptions.backups.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('resources.adminOptions.backups.description')}
      </Typography>

      <div style={backupPanelThemeVars(theme)}>
        <BackupPanel
          apiBase={apiBase}
          auth={auth}
          locale={locale === 'es' ? 'es-ES' : 'en'}
          appName="RoommatikAnalytics.API"
        />
      </div>
    </Box>
  );
};

export default BackupsView;
